import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useMonetization } from '../contexts/MonetizationContext';
import DisButton from '../components/design-system/DisButton';
import DisAvatar from '../components/design-system/DisAvatar';
import ChildrenManagementModal from '../components/ChildrenManagementModal';
import PlanLimitReachedModal from '../components/PlanLimitReachedModal';
import Footer from '../components/Footer';
import EntertainmentCarousel from '../components/EntertainmentCarousel';
import FloatingMenu from '../components/FloatingMenu';
import LoadingStoryModal from '../components/LoadingStoryModal';

const StoryPage = ({ onNavigateBack, onNavigateToHistory, onNavigateToSubscription, onNavigateToAdminPanel, onNavigateToChat, currentMode = 'story', apiEndpoint = '/stories/generate' }) => {
  const navigate = useNavigate();
  const { user, api, logout } = useAuth();
  const { 
    usage, 
    isPremium, 
    trialDaysRemaining, 
    storiesThisMonth, 
    maxStories, 
    canGenerateStory,
    storiesRemaining,
    bonusStories,
    totalStoriesRemaining,
    subscription,
    currentPlan
  } = useMonetization();
  const [showChildrenManagementModal, setShowChildrenManagementModal] = useState(false);
  const [showPlanLimitModal, setShowPlanLimitModal] = useState(false);
  
  // États pour les étapes de création d'histoire
  const [step, setStep] = useState(1);
  const [selectedChildren, setSelectedChildren] = useState([]);
  const [duration, setDuration] = useState(null);
  const [storyType, setStoryType] = useState(null);
  const [theme, setTheme] = useState(null);
  const [customTheme, setCustomTheme] = useState('');
  const [includeChildren, setIncludeChildren] = useState(true);
  const [children, setChildren] = useState([]);
  
  // États pour la génération et l'affichage
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedStory, setGeneratedStory] = useState(null);
  const [currentStoryPage, setCurrentStoryPage] = useState(0);
  
  // États pour les illustrations par page
  const [pageIllustrations, setPageIllustrations] = useState({});
  const [loadingIllustrations, setLoadingIllustrations] = useState({});
  const [isGeneratingRemainingImages, setIsGeneratingRemainingImages] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationProgressText, setGenerationProgressText] = useState("Initialisation...");
  
  // État pour le Wake Lock (empêcher verrouillage mobile)
  const [wakeLock, setWakeLock] = useState(null);
  
  // Référence pour le scroll automatique
  const nextSectionRef = useRef(null);
  const textScrollContainerRef = useRef(null);
  
  // Reset scroll en haut quand on change de page
  useEffect(() => {
    if (textScrollContainerRef.current) {
      textScrollContainerRef.current.scrollTop = 0;
    }
  }, [currentStoryPage]);
  
  // Effet pour gérer la visibilité de la page (détection verrouillage/arrière-plan)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.warn('📱 Page masquée - génération peut être interrompue sur mobile');
      } else {
        console.log('📱 Page visible - génération continue');
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      // Nettoyer Wake Lock au démontage du composant
      releaseWakeLock();
    };
  }, [wakeLock]);

  // Options pour les différentes étapes
  const durations = [
    { value: 5, label: '5 minutes', emoji: '⚡', description: 'Histoire courte' },
    { value: 10, label: '10 minutes', emoji: '📖', description: 'Histoire classique' },
    { value: 15, label: '15 minutes', emoji: '📚', description: 'Histoire détaillée' },
    { value: 20, label: '20 minutes', emoji: '📜', description: 'Grande aventure' }
  ];

  const storyTypes = [
    { value: 'sleep', label: 'Pour s\'endormir', emoji: '😴', description: 'Douce et apaisante' },
    { value: 'dream', label: 'Pour rêver', emoji: '✨', description: 'Magique et imaginaire' },
    { value: 'adventure', label: 'Pour partir à l\'aventure', emoji: '🗺️', description: 'Pleine d\'action' },
    { value: 'learn', label: 'Pour apprendre', emoji: '🎓', description: 'Éducative et instructive' },
    { value: 'laugh', label: 'Pour rigoler', emoji: '😄', description: 'Très rigolote avec jeux de mots et situations cocasses' },
    { value: 'courage', label: 'Pour être courageux', emoji: '🦸', description: 'Inspirante et motivante' },
    { value: 'discover', label: 'Pour découvrir le monde', emoji: '🌍', description: 'Exploratrice' },
    { value: 'imagine', label: 'Pour développer l\'imagination', emoji: '🎨', description: 'Créative et originale' }
  ];

  const themes = [
    { value: 'surprise', label: 'Thème surprise', emoji: '🎁', description: 'Laisse-moi choisir !' },
    { value: 'party', label: 'La fête', emoji: '🎉', description: 'Anniversaires et célébrations' },
    { value: 'school', label: 'L\'école', emoji: '🏫', description: 'Apprentissage et amitié' },
    { value: 'cars', label: 'Les voitures', emoji: '🚗', description: 'Bolides et courses' },
    { value: 'planes', label: 'Les avions', emoji: '✈️', description: 'Vol et voyage' },
    { value: 'knights_princesses', label: 'Chevaliers et princesses', emoji: '🏰', description: 'Châteaux et royauté' },
    { value: 'fairy_princesses', label: 'Princesses et contes de fées', emoji: '👸', description: 'Magie et féerie' },
    { value: 'animals', label: 'Les animaux', emoji: '🐾', description: 'Amis à quatre pattes' },
    { value: 'dinosaurs', label: 'Les dinosaures', emoji: '🦕', description: 'Préhistoire et fossiles' },
    { value: 'family', label: 'La famille', emoji: '👨‍👩‍👧‍👦', description: 'Amour et partage' },
    { value: 'unicorns', label: 'Les licornes', emoji: '🦄', description: 'Magie et arc-en-ciel' },
    { value: 'space', label: 'L\'espace et les étoiles', emoji: '🚀', description: 'Cosmos et planètes' },
    { value: 'superheroes', label: 'Les super-héros', emoji: '🦸‍♂️', description: 'Pouvoirs et justice' },
    { value: 'music', label: 'La musique et la danse', emoji: '🎵', description: 'Rythme et mélodie' },
    { value: 'robots', label: 'Les robots', emoji: '🤖', description: 'Technologie et futur' },
    { value: 'nature', label: 'La nature et les saisons', emoji: '🌳', description: 'Environnement et météo' },
    { value: 'pirates', label: 'Les pirates', emoji: '🏴‍☠️', description: 'Trésor et aventure' },
    { value: 'christmas', label: 'Noël', emoji: '🎄', description: 'Magie de Noël et fêtes' },
    { value: 'jobs', label: 'Les métiers', emoji: '👷', description: 'Professions et compétences' },
    { value: 'custom', label: 'Thème personnalisé', emoji: '✏️', description: 'Décris ton propre thème' }
  ];

  useEffect(() => {
    fetchChildren();
  }, []);

  useEffect(() => {
    if (step > 1 && nextSectionRef.current) {
      nextSectionRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  }, [step]);

  const fetchChildren = async () => {
    try {
      const response = await api.get('/children');
      setChildren(response.data?.children || []);
    } catch (error) {
      console.error('Erreur récupération enfants:', error);
    }
  };

  const toggleChildSelection = (childId) => {
    setSelectedChildren(prev => 
      prev.includes(childId) 
        ? prev.filter(id => id !== childId)
        : [...prev, childId]
    );
  };

  const handleChoiceAndNext = (choice, nextStep) => {
    switch (step) {
      case 2:
        setStoryType(choice);
        break;
      case 4:
        setTheme(choice);
        // Si c'est un thème personnalisé, ne pas passer automatiquement à l'étape suivante
        if (choice === 'custom') {
          return;
        }
        break;
    }
    setStep(nextStep);
  };

  const handleCustomThemeConfirm = () => {
    if (customTheme.trim().length > 0) {
      setStep(5);
    }
  };

  const generateProgressiveStoryChoice = () => {
    const selectedChildrenNames = selectedChildren
      .map(id => children.find(child => child.id === id)?.name)
      .filter(Boolean)
      .join(', ');
    
    let sentence = '';
    
    // Étape 1 : Enfants sélectionnés
    if (selectedChildren.length > 0) {
      sentence = `Je veux une histoire pour ${selectedChildrenNames}`;
    }
    
    // Étape 2 : + Durée
    if (duration) {
      const durationObj = durations.find(d => d.value === duration);
      sentence += ` à lire en ${durationObj?.label}`;
    }
    
    // Étape 3 : + Type
    if (storyType) {
      const storyTypeObj = storyTypes.find(st => st.value === storyType);
      sentence += ` ${storyTypeObj?.label.toLowerCase()}`;
    }
    
    // Étape 4 : + Thème  
    if (theme) {
      if (theme === 'custom' && customTheme) {
        sentence += ` sur le thème : "${customTheme.length > 40 ? customTheme.slice(0, 40) + '...' : customTheme}"`;
      } else {
        const themeObj = themes.find(t => t.value === theme);
        sentence += ` sur le thème ${themeObj?.label.toLowerCase()}`;
      }
    }
    
    return sentence + (sentence ? '.' : '');
  };

  // Plus besoin de validation frontend - inputs contrôlés par l'interface

  const generateStory = async () => {
    console.log('🚀 Début génération histoire...');
    
    // RESET complet de l'état de génération
    setIsGenerating(true);
    setGenerationProgress(0);
    setGenerationProgressText("🚀 Démarrage de la génération...");
    setGeneratedStory(null);
    setPageIllustrations({});
    setCurrentStoryPage(0);
    
    // Activer Wake Lock pour empêcher verrouillage mobile
    const lock = await requestWakeLock();
    
    try {
      const selectedChildrenData = selectedChildren.map(id => 
        children.find(child => child.id === id)
      ).filter(Boolean);

      console.log('📊 Données enfants sélectionnés:', selectedChildrenData);

      const storyPrompt = createStoryPrompt(selectedChildrenData);
      console.log('📝 Prompt créé:', storyPrompt);
      
      const requestData = {
        children: selectedChildrenData,
        duration: duration,
        story_type: storyType,
        theme: theme,
        custom_theme: theme === 'custom' ? customTheme : null,
        include_children: includeChildren,
        prompt: storyPrompt
      };
      
      console.log('📤 Envoi requête API:', requestData);
      
      // Démarrer la génération (retourne immédiatement un task_id)
      const response = await api.post(apiEndpoint, requestData);
      
      console.log('✅ Task ID reçu:', response.data.task_id);
      
      // Démarrer le polling pour vérifier le statut
      const taskId = response.data.task_id;
      pollGenerationStatus(taskId, lock);
      
    } catch (error) {
      console.error('❌ Erreur lors du démarrage de la génération:', error);
      setIsGenerating(false);
      
      // Libérer Wake Lock en cas d'erreur
      if (lock) {
        releaseWakeLock(lock);
      }
      
      // Stopper la simulation de progression
      if (window.progressSimulation) {
        clearInterval(window.progressSimulation);
      }
      
      setGenerationProgress(0);
      setGenerationProgressText('');
      
      if (error.response?.status === 403) {
        console.log('📊 Limite atteinte, affichage modal');
        setShowPlanLimitModal(true);
      } else {
        alert(`❌ Erreur: ${error.response?.data?.detail || error.message || 'Une erreur est survenue'}`);
      }
    }
  };

  const pollGenerationStatus = async (taskId, lock) => {
    console.log('🔄 Début polling pour task:', taskId);
    
    let pollAttempts = 0;
    const maxPollAttempts = 60; // 60 * 3s = 3 minutes max
    
    const pollInterval = setInterval(async () => {
      pollAttempts++;
      
      if (pollAttempts > maxPollAttempts) {
        console.error('⏰ Timeout polling - 3 minutes dépassées, chargement histoire partielle...');
        clearInterval(pollInterval);
        
        // Libérer Wake Lock
        if (lock) {
          releaseWakeLock(lock);
        }
        
        // Essayer de charger l'histoire depuis l'historique
        try {
          console.log('📚 Tentative de chargement depuis historique...');
          const historyResponse = await api.get('/stories/history');
          
          if (historyResponse.data && historyResponse.data.length > 0) {
            // Prendre la dernière histoire (la plus récente)
            const latestStory = historyResponse.data[0];
            console.log('✅ Histoire récente trouvée:', latestStory.id);
            
            // Charger l'histoire comme si la génération était terminée
            setGeneratedStory(latestStory);
            setCurrentStoryPage(0);
            setPageIllustrations(latestStory.page_illustrations || {});
            
            setGenerationProgress(100);
            setGenerationProgressText('✅ Histoire chargée (images en cours en arrière-plan)');
            
            setTimeout(() => {
              setIsGenerating(false);
              setGenerationProgress(0);
              setGenerationProgressText('');
              // Naviguer vers la page de lecture
              navigate('/story/view', { 
                state: { 
                  story: latestStory, 
                  pageIllustrations: latestStory.page_illustrations || {} 
                } 
              });
            }, 1000);
            
            return; // Sortir sans erreur
          }
        } catch (historyError) {
          console.error('❌ Erreur chargement historique:', historyError);
        }
        
        // Si on ne peut pas charger l'histoire, afficher un message informatif
        setIsGenerating(false);
        setGenerationProgress(0);
        setGenerationProgressText('');
        alert('⏰ La génération est en cours. Votre histoire sera disponible dans l\'historique dans quelques instants.');
        return;
      }
      
      try {
        console.log(`📡 Polling attempt ${pollAttempts}/${maxPollAttempts}...`);
        const statusResponse = await api.get(`/stories/generate/status/${taskId}`);
        const status = statusResponse.data;
        
        console.log('📊 Statut reçu:', status);
        
        // Mettre à jour la progression
        setGenerationProgress(status.progress || 0);
        
        // Mettre à jour le texte selon le statut
        if (status.status === 'pending') {
          setGenerationProgressText('🚀 Démarrage de la génération...');
        } else if (status.status === 'generating_text') {
          setGenerationProgressText('📝 Génération du texte de l\'histoire...');
        } else if (status.status === 'generating_images') {
          const imagesProgress = Math.floor((status.progress - 40) / 10);
          setGenerationProgressText(`🎨 Génération des illustrations (${imagesProgress}/3)...`);
        } else if (status.status === 'completed') {
          console.log('✅ Génération terminée!');
          clearInterval(pollInterval);
          
          // Libérer Wake Lock
          if (lock) {
            releaseWakeLock(lock);
          }
          
          setGenerationProgress(100);
          setGenerationProgressText('✅ Histoire prête !');
          
          // Extraire les données de l'histoire
          const storyData = status.story;
          
          if (storyData && (storyData.content || storyData.pages)) {
            console.log('✅ Histoire trouvée et valide:', storyData);
            
            // Gérer les illustrations par page
            const initialIllustrations = storyData.page_illustrations || {};
            console.log('🎨 Illustrations initiales reçues:', Object.keys(initialIllustrations));
            
            // Naviguer vers la page de lecture
            setTimeout(() => {
              setIsGenerating(false);
              setGenerationProgress(0);
              setGenerationProgressText('');
              navigate('/story/view', { 
                state: { 
                  story: storyData, 
                  pageIllustrations: initialIllustrations 
                } 
              });
            }, 1000);
          } else {
            console.error('❌ Structure d\'histoire invalide');
            clearInterval(pollInterval);
            setIsGenerating(false);
            alert('❌ Erreur: Structure d\'histoire invalide');
          }
          
        } else if (status.status === 'error') {
          console.error('❌ Erreur de génération:', status.error);
          clearInterval(pollInterval);
          
          // Libérer Wake Lock
          if (lock) {
            releaseWakeLock(lock);
          }
          
          // En cas d'erreur, essayer de récupérer une histoire partielle depuis l'historique
          try {
            console.log('📚 Tentative de récupération d\'histoire partielle...');
            const historyResponse = await api.get('/stories/history');
            
            if (historyResponse.data && historyResponse.data.length > 0) {
              const latestStory = historyResponse.data[0];
              // Vérifier si c'est une histoire récente (moins de 5 minutes)
              const storyTime = new Date(latestStory.created_at);
              const now = new Date();
              const minutesAgo = (now - storyTime) / (1000 * 60);
              
              if (minutesAgo < 5) {
                console.log('✅ Histoire partielle trouvée, redirection...');
                setIsGenerating(false);
                setGenerationProgress(0);
                setGenerationProgressText('');
                navigate('/story/view', { 
                  state: { 
                    story: latestStory, 
                    pageIllustrations: latestStory.page_illustrations || {},
                    isPartial: true
                  } 
                });
                return;
              }
            }
          } catch (historyError) {
            console.error('❌ Erreur récupération historique:', historyError);
          }
          
          // Si pas d'histoire récupérable, afficher l'erreur et rediriger vers l'historique
          setIsGenerating(false);
          setGenerationProgress(0);
          setGenerationProgressText('');
          alert(`⚠️ La génération a rencontré un problème. Consultez votre historique pour voir si l'histoire est disponible.`);
          navigate('/history');
        }
        
      } catch (error) {
        console.error('❌ Erreur lors du polling:', error);
        // Ne pas arrêter le polling pour une erreur réseau temporaire
        // Continuer à essayer
      }
    }, 3000); // Poll toutes les 3 secondes
    
    // Stocker l'interval pour pouvoir l'arrêter si besoin
    window.pollInterval = pollInterval;
  };
  
  const createStoryPrompt = (childrenData) => {
    const ages = childrenData.map(child => child.age);
    const minAge = Math.min(...ages);
    const maxAge = Math.max(...ages);
    
    const durationObj = durations.find(d => d.value === duration);
    const storyTypeObj = storyTypes.find(st => st.value === storyType);
    const themeObj = themes.find(t => t.value === theme);

    let prompt = `Crée une histoire pour enfant${childrenData.length > 1 ? 's' : ''} de ${minAge}${maxAge !== minAge ? ` à ${maxAge}` : ''} ans`;
    
    if (includeChildren) {
      const childrenNames = childrenData.map(child => child.name).join(' et ');
      prompt += `, mettant en scène ${childrenNames}`;
    } else {
      prompt += ` avec des personnages fictifs adaptés à leur âge`;
    }
    
    prompt += `. L'histoire doit durer environ ${duration} minutes de lecture et être ${storyTypeObj.description} sur le thème ${themeObj.description}. 

IMPÉRATIF - Structure narrative ET visuelle :
- Créer une histoire COHÉRENTE avec un début, un milieu plein de rebondissements, et une fin satisfaisante
- Les personnages doivent avoir des NOMS CONSTANTS et des caractéristiques visuelles FIXES (couleur cheveux, vêtements, etc.)
- Chaque page doit logiquement suivre la précédente avec une PROGRESSION narrative claire
- Ajouter des dialogues vivants et des émotions pour captiver l'enfant

${storyType === 'laugh' ? `
SPÉCIAL HISTOIRE RIGOLOTE - OBLIGATOIRE POUR FAIRE RIRE LES ENFANTS :
- NOMS ULTRA-RIGOLOS obligatoires : Vroum-Boum, Bazar-le-Lézard, Madame Zigzag, Monsieur Patate-Banane, Captain Chaussette
- TITRE HILARANT avec jeux de mots (ex: "L'Incroyable Course du Vroum-Boum qui fait Atchoum")
- ONOMATOPÉES À CHAQUE PAGE : Paf, Boum, Splatch, Pouic-pouic, Vroum-vroum, Atchoum, Hihi-haha, Bing-bang
- SITUATIONS ULTRA-COCASSES : personnages qui rotent, qui ont le hoquet, qui tombent de façon rigolote
- DIALOGUES ABSURDES : "J'ai mangé trop d'essence !", "Mes roues ont des chatouilles !", "Oups, j'ai éternué des bulles !"
- GAGS VISUELS à chaque page : chutes, zigzags, objets qui font des choses impossibles
- PÉRIPÉTIES LOUFOQUES : courses à l'envers, véhicules qui dansent, personnages qui parlent à l'envers
- L'histoire doit provoquer des ÉCLATS DE RIRE, pas seulement des sourires` : 
'- Inclure des éléments de SUSPENSE, d\'HUMOUR et de SURPRISE adaptés à l\'âge'}

IMPÉRATIF - Descriptions visuelles pour illustrations :
Tu dois OBLIGATOIREMENT inclure après l'histoire :

1. **DESCRIPTION PERSONNAGES PRINCIPAUX** (à maintenir identiques sur toutes les pages) :
   - Nom, âge, type (enfant/animal/créature)
   - Apparence physique détaillée (couleur cheveux/poils, taille, corpulence)
   - Vêtements caractéristiques (couleurs, style)
   - Traits distinctifs (lunettes, cicatrices, accessoires)

2. **DESCRIPTION DES LIEUX PRINCIPAUX** :
   - Environnements récurrents avec leurs caractéristiques visuelles
   - Couleurs dominantes, éléments décoratifs, ambiance

3. **DESCRIPTION ILLUSTRATION POUR CHAQUE PAGE** :
   - Action/scène principale à illustrer
   - Position et expression des personnages
   - Éléments du décor à inclure
   - Ambiance et émotion à transmettre

Format requis : Structure en pages courtes (2-4 phrases par page) puis les 3 sections de descriptions visuelles.`;
        
    
    return prompt;
  };

  // Fonction pour générer les illustrations restantes en arrière-plan
  const generateRemainingIllustrations = async (storyId) => {
    if (isGeneratingRemainingImages) return;
    
    setIsGeneratingRemainingImages(true);
    console.log('🎨 Démarrage génération illustrations restantes pour:', storyId);
    
    try {
      const response = await api.post(`/stories/${storyId}/generate-remaining-illustrations`);
      
      if (response.data && response.data.page_illustrations) {
        console.log('✅ Illustrations restantes générées:', Object.keys(response.data.page_illustrations));
        
        // Mettre à jour les illustrations page par page
        const updatedIllustrations = {
          ...pageIllustrations,
          ...response.data.page_illustrations
        };
        setPageIllustrations(updatedIllustrations);
        
        // Vérifier si toutes les images sont maintenant prêtes
        if (response.data.illustrations_status === 'complete') {
          console.log('🎉 TOUTES les illustrations sont prêtes - Affichage de l\'histoire');
          // Naviguer vers la page de lecture si on a une histoire générée
          if (generatedStory) {
            navigate('/story/view', { 
              state: { 
                story: generatedStory, 
                pageIllustrations: updatedIllustrations 
              } 
            });
          }
        }
      }
    } catch (error) {
      console.error('❌ Erreur génération illustrations restantes:', error);
    } finally {
      setIsGeneratingRemainingImages(false);
    }
  };

  // Fonctions pour gérer les limites et les packs d'histoires
  const handleGenerateAttempt = () => {
    if (!canGenerateStory) {
      setShowPlanLimitModal(true);
    } else {
      generateStory();
    }
  };

  const handleStartStoryCreation = () => {
    // Vérifier les limites AVANT de commencer le processus
    if (!canGenerateStory) {
      setShowPlanLimitModal(true);
    } else {
      // Si pas de limite, continuer normalement
      if (step !== 1) {
        setStep(1); // Recommencer le processus
      }
    }
  };

  // Fonction pour surveiller le progrès de génération
  const pollGenerationProgress = async (storyId) => {
    try {
      const response = await api.get(`/stories/${storyId}/progress`);
      const { progress, progress_text, is_complete } = response.data;
      
      console.log('📊 Progrès génération:', progress, progress_text);
      setGenerationProgress(progress);
      setGenerationProgressText(progress_text);
      
      if (is_complete) {
        console.log('🎉 Génération terminée - Arrêt du polling');
        return true; // Arrêter le polling
      }
      
      return false; // Continuer le polling
    } catch (error) {
      console.error('❌ Erreur récupération progrès:', error);
      return false;
    }
  };

  const handleNavigateToSubscription = (planId = null) => {
    setShowPlanLimitModal(false);
    if (planId) {
      // Naviguer vers la page subscription avec le plan pré-sélectionné
      onNavigateToSubscription({ preselectedPlan: planId });
    } else {
      onNavigateToSubscription();
    }
  };

  const handlePurchaseStoryPack = async (priceId, storyCount) => {
    try {
      // Intégrer la logique d'achat Stripe pour les packs d'histoires
      console.log(`Achat pack: ${storyCount} histoires (${priceId})`);
      
      const response = await api.post('/monetization/purchase-story-pack', {
        price_id: priceId,
        story_count: storyCount
      });
      
      if (response.data.checkout_url) {
        // Rediriger vers la page de paiement Stripe
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Erreur achat pack d\'histoires:', error);
      alert('Erreur lors de l\'achat du pack. Veuillez réessayer.');
    }
  };

  // Fonctions Wake Lock pour empêcher le verrouillage mobile
  const requestWakeLock = async () => {
    try {
      if ('wakeLock' in navigator) {
        const lock = await navigator.wakeLock.request('screen');
        setWakeLock(lock);
        console.log('🔒 Wake Lock activé - écran maintenu actif pendant génération');
        return lock;
      }
    } catch (error) {
      console.warn('⚠️ Wake Lock non supporté:', error);
    }
    return null;
  };

  const releaseWakeLock = async () => {
    if (wakeLock) {
      try {
        await wakeLock.release();
        setWakeLock(null);
        console.log('🔓 Wake Lock désactivé');
      } catch (error) {
        console.warn('⚠️ Erreur désactivation Wake Lock:', error);
      }
    }
  };

  const resetStoryCreation = () => {
    setStep(1);
    setSelectedChildren([]);
    setDuration(null);
    setStoryType(null);
    setTheme(null);
    setCustomTheme('');
    setIncludeChildren(true);
    setGeneratedStory(null);
    setCurrentStoryPage(0);
    setPageIllustrations({});
    setLoadingIllustrations({});
    setIsGeneratingRemainingImages(false);
    releaseWakeLock(); // Libérer le wake lock lors du reset
  };

  const renderChoiceButtons = (options, selectedValue, onSelect, isMultiple = false) => {
    // Pour les enfants, permettre la sélection multiple avec bouton Confirmer
    if (isMultiple) {
      return (
        <div className="space-y-4">
          <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
            {options.map((option) => (
              <button
                key={option.value}
                className={`px-6 py-3 rounded-full transition-all duration-200 border-2 ${
                  selectedChildren.includes(option.value)
                    ? 'text-white shadow-lg scale-105 border-transparent'
                    : 'bg-white border-[rgba(91,191,189,0.3)] text-[#333333] hover:border-[#5BBFBD] hover:shadow-md'
                }`}
                style={{ 
                  fontFamily: '"Caveat", cursive', 
                  fontSize: '1.5rem',
                  background: selectedChildren.includes(option.value) 
                    ? 'linear-gradient(135deg, #4A90D9 0%, #5BBFBD 100%)' 
                    : undefined
                }}
                onClick={() => toggleChildSelection(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
          
          {/* Bouton Confirmer - visible uniquement si au moins un enfant sélectionné */}
          {selectedChildren.length > 0 && (
            <div className="text-center mt-4">
              <button
                onClick={() => {
                  if (!canGenerateStory) {
                    setShowPlanLimitModal(true);
                  } else {
                    setDuration(5); // Durée fixée à 5 min par défaut
                    setStep(2); // Passer directement au type d'histoire
                  }
                }}
                className="px-8 py-3 text-white rounded-full font-medium shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200"
                style={{ 
                  fontFamily: '"Caveat", cursive', 
                  fontSize: '1.4rem',
                  background: 'linear-gradient(135deg, #4A90A4 0%, #6BB5A2 100%)'
                }}
              >
                ✓ Confirmer {selectedChildren.length > 1 ? `(${selectedChildren.length} enfants)` : ''}
              </button>
            </div>
          )}
        </div>
      );
    }
    
    // Pour les autres options (durée, type, thème), nouveau style épuré avec boutons arrondis
    // Style app mobile : pills blanches avec bordure teal
    return (
      <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
        {options.map((option) => (
          <button
            key={option.value}
            className={`px-5 py-3 rounded-full transition-all duration-200 border-2 ${
              selectedValue === option.value
                ? 'bg-[rgba(74,144,164,0.1)] border-[#4A90A4] text-[#4A90A4] shadow-md scale-105'
                : 'bg-white border-[rgba(91,191,189,0.3)] text-[#333333] hover:border-[#5BBFBD] hover:shadow-md'
            }`}
            onClick={() => onSelect(option.value)}
          >
            <span className="text-lg mr-1">{option.emoji}</span>
            <span style={{ fontFamily: '"Caveat", cursive', fontSize: '1.25rem' }}>{option.label}</span>
          </button>
        ))}
      </div>
    );
  };

  return (
    <>
      <style>
        {`@import url('https://fonts.googleapis.com/css2?family=Caveat:wght@700&display=swap');`}
      </style>
      
      <div className="min-h-screen relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #B8D4E3 0%, #A7D3D8 25%, #9ECDC4 50%, #A8D5BA 75%, #B5DCAE 100%)' }}>
        
        {/* Bulle de texte style BD */}
        <div className="absolute left-4 sm:left-8 top-4 sm:top-8 z-20">
          <div className="relative">
            <svg viewBox="0 0 220 90" className="w-[140px] sm:w-[180px] h-auto" style={{ filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.15))' }}>
              <path d="M 20,45 C 20,20 40,8 80,8 C 120,5 160,10 190,20 C 210,28 215,45 210,60 C 205,75 180,82 140,85 C 100,88 60,85 35,75 L 15,88 L 25,70 C 18,65 20,55 20,45 Z" fill="white" stroke="#374151" strokeWidth="3"/>
            </svg>
            <p className="absolute top-1/2 left-1/2 text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 whitespace-nowrap" style={{ fontFamily: '"Caveat", cursive', transform: 'translate(-52%, -45%) rotate(-1deg)' }}>
              Dis Maman !
            </p>
          </div>
        </div>

        {/* Mascotte avec livre - Même taille que ChatPage, en arrière-plan */}
        <div className="absolute right-0 top-0 z-0 pointer-events-none">
          <img 
            src="https://customer-assets.emergentagent.com/job_kidchat-5/artifacts/ussd487b_image.png"
            alt="Mascotte"
            className="h-[340px] sm:h-[440px] md:h-[520px] lg:h-[600px] object-contain"
            style={{ filter: 'drop-shadow(0 10px 25px rgba(0,0,0,0.2))' }}
          />
        </div>
        
        {/* Contenu principal - avec padding-bottom pour le récapitulatif et menu flottants */}
        <div className="container mx-auto px-3 py-4 pt-28 sm:pt-32 pb-48 relative z-10">
          {step === 1 && (
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-6">
                <h2 
                  className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2" 
                  style={{ fontFamily: '"Caveat", cursive', textShadow: '0 2px 4px rgba(255,255,255,0.5)' }}
                >
                  Créez une histoire
                </h2>
                <p className="text-gray-600 text-sm">Sélectionne un ou plusieurs enfants</p>
              </div>

            {children.length > 0 ? (
              <>
                {renderChoiceButtons(
                  children.map(child => ({
                    value: child.id,
                    label: child.name,
                    emoji: child.gender === 'boy' ? '🧒' : child.gender === 'girl' ? '👧' : '😊',
                    description: `${child.age} ans`,
                    age: child.age,
                    gender: child.gender
                  })),
                  null,
                  null,
                  true
                )}
              </>
            ) : (
              <div className="text-center">
                <p className="text-gray-700 mb-4 font-medium" style={{ textShadow: '0 1px 2px rgba(255,255,255,0.5)' }}>Aucun enfant trouvé. Vous devez d'abord créer un profil d'enfant.</p>
                <DisButton onClick={onNavigateBack} className="rounded-xl shadow-lg">
                  Retour pour créer un enfant
                </DisButton>
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div ref={nextSectionRef} className="max-w-4xl mx-auto mt-6">
            {/* Bouton retour */}
            <button
              onClick={() => setStep(1)}
              className="mb-4 flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors mx-auto"
            >
              <span className="text-lg">←</span>
              <span style={{ fontFamily: '"Caveat", cursive', fontSize: '1.1rem' }}>Retour</span>
            </button>
            
            <div className="text-center mb-6">
              <h2 
                className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2" 
                style={{ fontFamily: '"Caveat", cursive', textShadow: '0 2px 4px rgba(255,255,255,0.5)' }}
              >
                Quel type d'histoire ?
              </h2>
              <p className="text-gray-600 text-sm">Choisis l'ambiance</p>
            </div>

            {renderChoiceButtons(storyTypes, storyType, (value) => handleChoiceAndNext(value, 3))}
          </div>
        )}

        {step === 3 && (
          <div ref={nextSectionRef} className="max-w-4xl mx-auto mt-6">
            {/* Bouton retour */}
            <button
              onClick={() => setStep(2)}
              className="mb-4 flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors mx-auto"
            >
              <span className="text-lg">←</span>
              <span style={{ fontFamily: '"Caveat", cursive', fontSize: '1.1rem' }}>Retour</span>
            </button>
            
            <div className="text-center mb-6">
              <h2 
                className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2" 
                style={{ fontFamily: '"Caveat", cursive', textShadow: '0 2px 4px rgba(255,255,255,0.5)' }}
              >
                Les personnages
              </h2>
              <p className="text-gray-600 text-sm mb-2">
                Inclure {selectedChildren.length === 1 ? 
                  children.find(c => c.id === selectedChildren[0])?.name : 
                  'les enfants'} dans l'histoire ?
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 max-w-md mx-auto">
              {/* Bouton Oui - Gradient teal (style app mobile) */}
              <button
                onClick={() => {
                  setIncludeChildren(true);
                  setStep(4);
                }}
                className="text-white px-6 py-4 rounded-2xl font-bold text-lg hover:opacity-90 transition-all duration-200 shadow-lg active:scale-[0.98]"
                style={{ 
                  background: 'linear-gradient(135deg, #4A90A4 0%, #5BBFBD 100%)',
                  fontFamily: '"Caveat", cursive',
                  fontSize: '1.4rem'
                }}
              >
                ✨ Oui, inclure dans l'histoire !
              </button>
              
              {/* Bouton Non - Gradient violet (style app mobile) */}
              <button
                onClick={() => {
                  setIncludeChildren(false);
                  setStep(4);
                }}
                className="text-white px-6 py-4 rounded-2xl font-bold text-lg hover:opacity-90 transition-all duration-200 shadow-lg active:scale-[0.98]"
                style={{ 
                  background: 'linear-gradient(135deg, #7C5CBF 0%, #A78BFA 100%)',
                  fontFamily: '"Caveat", cursive',
                  fontSize: '1.4rem'
                }}
              >
                📚 Histoire avec d'autres personnages
              </button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div ref={nextSectionRef} className="max-w-4xl mx-auto mt-6">
            {/* Bouton retour */}
            <button
              onClick={() => setStep(3)}
              className="mb-4 flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors mx-auto"
            >
              <span className="text-lg">←</span>
              <span style={{ fontFamily: '"Caveat", cursive', fontSize: '1.1rem' }}>Retour</span>
            </button>
            
            <div className="text-center mb-6">
              <h2 
                className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2" 
                style={{ fontFamily: '"Caveat", cursive', textShadow: '0 2px 4px rgba(255,255,255,0.5)' }}
              >
                Quel thème ?
              </h2>
              <p className="text-gray-600 text-sm">Choisis l'univers de l'histoire</p>
            </div>

            {renderChoiceButtons(themes, theme, (value) => handleChoiceAndNext(value, 5))}
            
            {/* Zone de texte pour thème personnalisé */}
            {theme === 'custom' && (
              <div className="mt-6 max-w-xl mx-auto">
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 border border-teal-200 shadow-lg">
                  <label className="block text-gray-700 font-medium mb-2 text-center" style={{ fontFamily: '"Caveat", cursive', fontSize: '1.2rem' }}>
                    ✏️ Décris ton thème personnalisé
                  </label>
                  <textarea
                    value={customTheme}
                    onChange={(e) => setCustomTheme(e.target.value.slice(0, 200))}
                    placeholder="Ex: Une aventure dans une forêt enchantée avec des lutins..."
                    className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 resize-none"
                    rows={3}
                    maxLength={200}
                  />
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-sm text-gray-500">{customTheme.length}/200 caractères</span>
                    <button
                      onClick={handleCustomThemeConfirm}
                      disabled={customTheme.trim().length === 0}
                      className={`px-6 py-2 rounded-full font-medium transition-all duration-200 ${
                        customTheme.trim().length > 0
                          ? 'bg-gradient-to-r from-blue-500 to-teal-500 text-white shadow-lg hover:shadow-xl hover:scale-105'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                      style={{ fontFamily: '"Caveat", cursive', fontSize: '1.1rem' }}
                    >
                      ✓ Valider mon thème
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {step === 5 && (
          <div ref={nextSectionRef} className="max-w-4xl mx-auto mt-6">
            {/* Bouton retour */}
            <button
              onClick={() => setStep(4)}
              className="mb-4 flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors mx-auto"
            >
              <span className="text-lg">←</span>
              <span style={{ fontFamily: '"Caveat", cursive', fontSize: '1.1rem' }}>Retour</span>
            </button>
            
            <div className="text-center">
              <h2 
                className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4" 
                style={{ fontFamily: '"Caveat", cursive', textShadow: '0 2px 4px rgba(255,255,255,0.5)' }}
              >
                C'est parti !
              </h2>
              
              {/* Récapitulatif avec mascotte - Style app mobile */}
              <div className="bg-white rounded-3xl p-6 mb-6 max-w-xl mx-auto shadow-md">
                {/* Mascotte mini avec bulle */}
                <div className="flex items-start gap-4 mb-4">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_dialogue-digest-1/artifacts/17m8qo3i_image.png"
                    alt="Mascotte"
                    className="w-20 h-20 object-contain flex-shrink-0"
                    style={{ filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.15))' }}
                  />
                  <div className="flex-1 bg-gray-50 rounded-xl p-3 relative">
                    {/* Pointe de bulle vers la mascotte */}
                    <div className="absolute left-0 top-4 w-0 h-0 border-t-8 border-b-8 border-r-8 border-transparent border-r-gray-50 -translate-x-2"></div>
                    {/* Phrase récapitulative avec couleurs inline - Style app mobile */}
                    <p className="text-[#333333] font-medium text-sm sm:text-base leading-relaxed">
                      📖 Je veux une histoire pour{' '}
                      <span className="font-bold" style={{ color: '#4A90A4' }}>
                        {selectedChildren.map(id => children.find(c => c.id === id)?.name).filter(Boolean).join(' et ')}
                      </span>
                      {storyType && (
                        <>, <span className="font-bold" style={{ color: '#F59E0B' }}>
                          {storyTypes.find(st => st.value === storyType)?.label.toLowerCase()}
                        </span></>
                      )}
                      {theme && theme !== 'custom' && (
                        <>, sur le thème <span className="font-bold" style={{ color: '#EF4444' }}>
                          {themes.find(t => t.value === theme)?.label.toLowerCase()}
                        </span></>
                      )}
                      {theme === 'custom' && customTheme && (
                        <>, sur le thème <span className="font-bold" style={{ color: '#EF4444' }}>
                          "{customTheme}"
                        </span></>
                      )}
                      {includeChildren && selectedChildren.length > 0 && (
                        <>, avec <span className="font-bold" style={{ color: '#4A90A4' }}>
                          {selectedChildren.map(id => children.find(c => c.id === id)?.name).filter(Boolean).join(' et ')}
                        </span> comme {selectedChildren.length > 1 ? 'personnages' : 'personnage'}</>
                      )}
                      .
                    </p>
                  </div>
                </div>
              </div>

              {/* Compteur d'histoires détaillé - 3 colonnes */}
              <div className="mb-4 max-w-lg mx-auto">
                <div className="grid grid-cols-3 gap-2">
                  {/* Quota mensuel */}
                  <div className="bg-white/70 backdrop-blur-sm rounded-xl p-3 text-center border border-teal-200">
                    <div className="text-xl mb-1">📖</div>
                    <div className="text-lg font-bold text-teal-800">
                      {storiesRemaining} sur {maxStories === 'unlimited' ? '∞' : maxStories}
                    </div>
                    <div className="text-xs text-gray-600">/mois</div>
                  </div>
                  
                  {/* Bonus packs - affiché uniquement si > 0 */}
                  {bonusStories > 0 ? (
                    <div className="bg-white/70 backdrop-blur-sm rounded-xl p-3 text-center border-2 border-amber-400">
                      <div className="text-xl mb-1">🎁</div>
                      <div className="text-lg font-bold text-amber-600">
                        {bonusStories}
                      </div>
                      <div className="text-xs text-gray-600">bonus (packs)</div>
                    </div>
                  ) : (
                    <div className="bg-white/40 backdrop-blur-sm rounded-xl p-3 text-center border border-gray-200">
                      <div className="text-xl mb-1 opacity-40">🎁</div>
                      <div className="text-lg font-bold text-gray-400">0</div>
                      <div className="text-xs text-gray-400">bonus (packs)</div>
                    </div>
                  )}
                  
                  {/* Total disponible */}
                  <div className="bg-white/70 backdrop-blur-sm rounded-xl p-3 text-center border-2 border-teal-500">
                    <div className="text-xl mb-1">✨</div>
                    <div className="text-lg font-bold text-teal-700">
                      {totalStoriesRemaining}
                    </div>
                    <div className="text-xs text-gray-600">disponible{totalStoriesRemaining > 1 ? 's' : ''}</div>
                  </div>
                </div>
                
                {/* Message d'info si bonus > 0 */}
                {bonusStories > 0 && (
                  <div className="mt-2 text-center">
                    <p className="text-xs text-gray-600 bg-white/50 rounded-lg px-3 py-1.5 inline-block">
                      💡 Quota mensuel utilisé en premier, puis vos packs bonus
                    </p>
                  </div>
                )}
                
                {/* Message d'erreur si limite atteinte */}
                {!canGenerateStory && totalStoriesRemaining === 0 && (
                  <div className="mt-2 text-center">
                    <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-1.5 inline-block">
                      ⚠️ Limite atteinte - Passez à un plan supérieur ou achetez un pack
                    </p>
                  </div>
                )}
              </div>

              {isGenerating ? (
                <div className="py-8">
                  {/* Carrousel de divertissement - Remplace le grand spinner */}
                  <div className="mb-6">
                    <EntertainmentCarousel 
                      children={selectedChildren.map(id => children.find(child => child.id === id)).filter(Boolean)}
                      isVisible={true}
                    />
                  </div>
                  
                  {/* Petit spinner et texte de progression en-dessous du carrousel */}
                  <div className="flex items-center justify-center space-x-3 mb-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-500"></div>
                    <p className="text-gray-600 text-sm font-medium">Génération en cours...</p>
                  </div>
                  
                  {/* Barre de progression */}
                  <div className="w-full max-w-md mx-auto mb-6">
                    <div className="flex justify-between items-center mb-2">
                      <p className="text-sm text-gray-600">{generationProgressText}</p>
                      <span className="text-sm font-semibold text-teal-600">{Math.round(generationProgress)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-blue-500 to-teal-500 rounded-full transition-all duration-500 ease-out"
                        style={{ width: `${generationProgress}%` }}
                      ></div>
                    </div>
                    
                    {/* Liste des étapes détaillées */}
                    <div className="mt-4 space-y-2">
                      {/* Étape 1: Génération du texte */}
                      <div className="flex items-center space-x-3">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                          generationProgress >= 20 
                            ? 'bg-green-500 text-white' 
                            : generationProgress > 0 
                              ? 'bg-teal-500 text-white animate-pulse'
                              : 'bg-gray-300 text-gray-600'
                        }`}>
                          {generationProgress >= 20 ? '✓' : '📝'}
                        </div>
                        <span className={`text-sm ${
                          generationProgress >= 20 ? 'text-green-600 font-medium' : 'text-gray-600'
                        }`}>
                          Génération de l'histoire
                        </span>
                      </div>
                      
                      {/* Étape 2: Création des illustrations */}
                      <div className="flex items-center space-x-3">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                          generationProgress >= 100 
                            ? 'bg-green-500 text-white' 
                            : generationProgress > 20 
                              ? 'bg-teal-500 text-white animate-pulse'
                              : 'bg-gray-300 text-gray-600'
                        }`}>
                          {generationProgress >= 100 ? '✓' : '🎨'}
                        </div>
                        <span className={`text-sm ${
                          generationProgress >= 100 ? 'text-green-600 font-medium' : 'text-gray-600'
                        }`}>
                          {(() => {
                            // Calculer le nombre d'images basé sur le progrès
                            const imagesCompleted = Math.max(0, Math.floor(((generationProgress - 20) / 80) * (generatedStory?.pages?.length || 3)));
                            const totalImages = generatedStory?.pages?.length || 3;
                            
                            if (generationProgress >= 100) {
                              return `Création des illustrations (${totalImages}/${totalImages})`;
                            } else if (generationProgress > 20) {
                              return `Création des illustrations (${imagesCompleted}/${totalImages})`;
                            } else {
                              return `Création des illustrations (0/${totalImages})`;
                            }
                          })()}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-500 mb-2">✨ Génération du texte et des illustrations</p>
                  <p className="text-xs text-gray-400 mt-2">Cela peut prendre 1-2 minutes</p>
                  <div className="mt-4 p-3 bg-white/60 backdrop-blur-sm rounded-xl max-w-md mx-auto">
                    <p className="text-xs text-blue-700 text-center">
                      📱 <strong>Sur mobile :</strong> Gardez votre écran allumé pour éviter l'interruption
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <DisButton
                    onClick={handleGenerateAttempt}
                    className="px-8 py-4 text-lg bg-gradient-to-r from-blue-500 to-teal-500 text-white rounded-xl shadow-lg"
                  >
                    ✨ Créer l'histoire
                  </DisButton>
                  
                  <div>
                    <DisButton
                      onClick={resetStoryCreation}
                      variant="ghost"
                      className="text-gray-700"
                      style={{ textShadow: '0 1px 2px rgba(255,255,255,0.5)' }}
                    >
                      🔄 Recommencer
                    </DisButton>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal de gestion des enfants */}
      <ChildrenManagementModal
        isOpen={showChildrenManagementModal}
        onClose={() => setShowChildrenManagementModal(false)}
        children={children}
        onSelectChild={() => {}} // Pas besoin de sélection dans StoryPage
        onAddChild={() => {
          // TODO: Ouvrir modal d'ajout d'enfant si nécessaire
          setShowChildrenManagementModal(false);
        }}
        onDeleteChild={async (childId) => {
          try {
            await api.delete(`/children/${childId}`);
            fetchChildren(); // Rafraîchir la liste
          } catch (error) {
            console.error('Erreur suppression enfant:', error);
            alert('Erreur lors de la suppression de l\'enfant');
          }
        }}
      />

      {/* Modal limite de plan atteinte */}
      <PlanLimitReachedModal
        isOpen={showPlanLimitModal}
        onClose={() => setShowPlanLimitModal(false)}
        subscription={subscription}
        currentPlan={currentPlan}
        storiesThisMonth={storiesThisMonth}
        maxStories={maxStories}
        onNavigateToSubscription={handleNavigateToSubscription}
        onPurchaseStoryPack={handlePurchaseStoryPack}
      />
      
      {/* Récapitulatif flottant en bas - visible seulement pendant la sélection */}
      {selectedChildren.length > 0 && step < 5 && !isGenerating && (
        <div className="fixed bottom-20 left-1/2 transform -translate-x-1/2 z-30 w-[90%] max-w-md">
          <div className="bg-white/90 backdrop-blur-md rounded-full px-5 py-3 shadow-xl border border-teal-200">
            <p className="text-teal-800 text-sm font-medium text-center truncate">
              📖 {generateProgressiveStoryChoice()}
            </p>
          </div>
        </div>
      )}
      
      {/* Modal de génération d'histoire - Style app mobile */}
      <LoadingStoryModal
        isVisible={isGenerating}
        progress={generationProgress}
        currentStep={generationProgressText}
        storiesUsed={storiesThisMonth}
        storiesMax={maxStories || 10}
        bonusStories={bonusStories || 0}
        storiesRemaining={storiesRemaining || 0}
      />
      
      {/* Menu flottant */}
      <FloatingMenu />
      
      {/* Footer avec déconnexion */}
      <Footer onLogout={logout} />
    </div>
    </>
  );
};

export default StoryPage;